<script setup>
import { ref, computed } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useEmitter } from 'dashboard/composables/emitter';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';

import WootDropdownItem from 'shared/components/ui/dropdown/DropdownItem.vue';
import WootDropdownMenu from 'shared/components/ui/dropdown/DropdownMenu.vue';
import wootConstants from 'dashboard/constants/globals';
import {
  CMD_REOPEN_CONVERSATION,
  CMD_RESOLVE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

import ButtonGroup from 'dashboard/components-next/buttonGroup/ButtonGroup.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();

const arrowDownButtonRef = ref(null);
const isLoading = ref(false);

const [showActionsDropdown, toggleDropdown] = useToggle();
const closeDropdown = () => toggleDropdown(false);
const openDropdown = () => toggleDropdown(true);

const currentChat = computed(() => getters.getSelectedChat.value);

const isOpen = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.OPEN
);
const isPending = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.PENDING
);
const isResolved = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.RESOLVED
);
const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);
// MakerHub: same "handled" predicate the Urgent/Pending getters trust — flips true
// the instant Resolve commits (reactive custom_attributes merge), survives refresh,
// and self-heals back to false when a newer customer message bumps last_activity_at.
// The explicit overrides (priority=urgent / pin_pending, set manually or by patrol)
// negate it: a conversation pinned back into a red tab is NOT handled, even though
// Set-to-Urgent/Pending stamps mh_handled_at to clear the derived membership first.
const isHandled = computed(() => {
  if (currentChat.value.priority === 'urgent') return false;
  const attrs = currentChat.value.custom_attributes || {};
  if (attrs.pin_pending === true || attrs.pin_pending === 'true') return false;
  const handledAt = attrs.mh_handled_at || 0;
  return (
    handledAt > 0 && handledAt >= (currentChat.value.last_activity_at || 0)
  );
});

const showAdditionalActions = computed(
  () => !isPending.value && !isSnoozed.value
);

const showOpenButton = computed(() => {
  return isPending.value || isSnoozed.value;
});

const getConversationParams = () => {
  const allConversations = document.querySelectorAll(
    '.conversations-list .conversation'
  );

  const activeConversation = document.querySelector(
    'div.conversations-list div.conversation.active'
  );
  const activeConversationIndex = [...allConversations].indexOf(
    activeConversation
  );
  const lastConversationIndex = allConversations.length - 1;

  return {
    all: allConversations,
    activeIndex: activeConversationIndex,
    lastIndex: lastConversationIndex,
  };
};

const toggleStatus = (status, snoozedUntil) => {
  closeDropdown();
  isLoading.value = true;

  store
    .dispatch('toggleStatus', {
      conversationId: currentChat.value.id,
      status,
      snoozedUntil,
    })
    .then(() => {
      useAlert(t('CONVERSATION.CHANGE_STATUS'));
      isLoading.value = false;
    });
};

// MakerHub: dropdown replaces upstream Snooze with manual Urgent/Pending placement —
// the same explicit overrides the AI patrol uses (priority=urgent / pin_pending),
// dispatched through the mark_handled-first store actions so the conversation moves
// to exactly one red tab. Snooze stays reachable via the card context menu / Cmd+K.
const markUrgent = () => {
  closeDropdown();
  isLoading.value = true;
  store
    .dispatch('markConversationUrgent', currentChat.value.id)
    .then(() => {
      useAlert(t('CONVERSATION.MARK_URGENT_SUCCESS'));
    })
    .catch(() => {})
    .finally(() => {
      isLoading.value = false;
    });
};

const markPending = () => {
  closeDropdown();
  isLoading.value = true;
  store
    .dispatch('markConversationPending', currentChat.value.id)
    .then(() => {
      useAlert(t('CONVERSATION.MARK_PENDING_SUCCESS'));
    })
    .catch(() => {})
    .finally(() => {
      isLoading.value = false;
    });
};

const onCmdOpenConversation = () => {
  toggleStatus(wootConstants.STATUS_TYPE.OPEN);
};

// MakerHub: "Resolve" no longer flips Chatwoot status — it marks the conversation
// handled (clears Urgent/Pending) while keeping it open and in the All tab. A newer
// customer message re-urgents it (see Conversation.mh_urgent).
const markHandled = () => {
  closeDropdown();
  isLoading.value = true;
  store
    .dispatch('markConversationHandled', currentChat.value.id)
    .then(() => {
      useAlert(t('CONVERSATION.MARK_HANDLED_SUCCESS'));
      isLoading.value = false;
    })
    .catch(() => {
      isLoading.value = false;
    });
};

const onCmdResolveConversation = () => {
  markHandled();
};

const keyboardEvents = {
  'Alt+KeyM': {
    action: () => arrowDownButtonRef.value?.$el.click(),
    allowOnFocusedInput: true,
  },
  'Alt+KeyE': {
    action: async () => {
      onCmdResolveConversation();
    },
  },
  '$mod+Alt+KeyE': {
    action: async event => {
      const { all, activeIndex, lastIndex } = getConversationParams();
      onCmdResolveConversation();

      if (activeIndex < lastIndex) {
        all[activeIndex + 1].click();
      } else if (all.length > 1) {
        all[0].click();
        document.querySelector('.conversations-list').scrollTop = 0;
      }
      event.preventDefault();
    },
  },
};

useKeyboardEvents(keyboardEvents);

useEmitter(CMD_REOPEN_CONVERSATION, onCmdOpenConversation);
useEmitter(CMD_RESOLVE_CONVERSATION, onCmdResolveConversation);
</script>

<template>
  <div class="flex relative justify-end items-center resolve-actions">
    <ButtonGroup
      class="flex-shrink-0 rounded-lg shadow outline-1 outline"
      :class="!showOpenButton ? 'outline-n-container' : 'outline-transparent'"
    >
      <Button
        v-if="isOpen"
        :label="
          isHandled
            ? t('CONVERSATION.HEADER.RESOLVED_LABEL')
            : t('CONVERSATION.HEADER.RESOLVE_ACTION')
        "
        size="sm"
        :color="isHandled ? 'teal' : 'slate'"
        no-animation
        class="ltr:rounded-r-none rtl:rounded-l-none !outline-0"
        :is-loading="isLoading"
        @click="onCmdResolveConversation"
      />
      <Button
        v-else-if="isResolved"
        :label="t('CONVERSATION.HEADER.REOPEN_ACTION')"
        size="sm"
        color="slate"
        no-animation
        class="ltr:rounded-r-none rtl:rounded-l-none !outline-0"
        :is-loading="isLoading"
        @click="onCmdOpenConversation"
      />
      <Button
        v-else-if="showOpenButton"
        :label="t('CONVERSATION.HEADER.OPEN_ACTION')"
        size="sm"
        color="slate"
        no-animation
        :is-loading="isLoading"
        @click="onCmdOpenConversation"
      />
      <Button
        v-if="showAdditionalActions"
        ref="arrowDownButtonRef"
        icon="i-lucide-chevron-down"
        :disabled="isLoading"
        size="sm"
        no-animation
        class="ltr:rounded-l-none rtl:rounded-r-none !outline-0"
        color="slate"
        trailing-icon
        @click="openDropdown"
      />
    </ButtonGroup>
    <div
      v-if="showActionsDropdown"
      v-on-clickaway="closeDropdown"
      class="border rounded-lg shadow-lg border-n-strong dark:border-n-strong box-content p-2 w-fit z-10 bg-n-alpha-3 backdrop-blur-[100px] absolute block left-auto top-full mt-0.5 start-0 xl:start-auto xl:end-0 max-w-[12.5rem] min-w-[9.75rem] [&_ul>li]:mb-0"
    >
      <WootDropdownMenu class="mb-0">
        <WootDropdownItem>
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.URGENT')"
            ghost
            slate
            sm
            start
            icon="i-lucide-flame"
            class="w-full"
            @click="markUrgent"
          />
        </WootDropdownItem>
        <WootDropdownItem>
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.PENDING')"
            ghost
            slate
            sm
            start
            icon="i-lucide-clock"
            class="w-full"
            @click="markPending"
          />
        </WootDropdownItem>
      </WootDropdownMenu>
    </div>
  </div>
</template>
